import { createTheme, tooltipClasses } from '@mui/material';

export const invertedColorsClass = 'Mds-invertedColors';

export const SKIP_INVERTED_COLORS_ATTR = 'data-skip-inverted-colors';

/**
 * Update root stylesheet to support color inversion.
 *
 * For more details, see https://mui.com/joy-ui/main-features/color-inversion/ and its
 * implementation in Joy UI.
 *
 * This updates the theme selector to e.g.:
 *
 * ```css
 * [data-color-scheme="dark"],
 * [data-color-scheme="light"] .Mds-invertedColors > *,
 * [data-color-scheme="light"] .MuiAutocomplete-option[aria-selected=true] > * {
 *   color-scheme: dark;
 *   --mds-palette-primary-main: #793690;
 *   ...more color scheme specific tokens...
 * }
 * ```
 */
export const enableColorInversion = <T extends ReturnType<typeof createTheme>>(theme: T): T => {
  const generateStyleSheets = () => {
    const original = theme.generateStyleSheets();

    // getColorSchemeSelector() returns e.g. `[data-color-scheme="light"] &` or `.light
    // &`. MDS should expect Theme.colorSchemeSelector to be anything - it is set by the
    // application.
    const lightSelector = theme.getColorSchemeSelector('light').replace(' &', '');
    // darkSelector ~ `[data-color-scheme="dark"]`
    const darkSelector = theme.getColorSchemeSelector('dark').replace(' &', '');

    return original.map((nested) => {
      const entries = Object.entries(nested);
      return Object.fromEntries(
        entries.map(([selector, css]) => {
          let invertedSelector;
          if (selector.includes(lightSelector)) {
            invertedSelector = darkSelector;
          }
          if (selector.includes(darkSelector)) {
            invertedSelector = lightSelector;
          }
          if (invertedSelector) {
            const descendants = `:not([${SKIP_INVERTED_COLORS_ATTR}], [${SKIP_INVERTED_COLORS_ATTR}] *)`;
            const newSelector = [
              selector,
              `${invertedSelector} .${invertedColorsClass} ${descendants}`,
              `${invertedSelector} .${tooltipClasses.tooltip} ${descendants}`,
            ].join(', ');
            return [newSelector, css];
          }
          return [selector, css];
        }),
      ) as Record<string, string>;
    });
  };
  return { ...theme, generateStyleSheets };
};
